import React from 'react'
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Chip, useTheme } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { type ISurveyItem } from '../../../../models/SurveyTemplate'
import { green, grey, shade } from '../../../../colors'
import { type IReportSurveySection } from '../../../../models/Report'
import { getSurveySectionCardSx, surveySectionListSx, surveySectionSummaryLayoutSx } from './surveySectionCardStyles'

interface SurveySectionAccordionProps {
  surveySections: any[]
  countNonDescriptiveQuestions: (surveyItems: ISurveyItem[]) => number
  renderItems: (items: any[], section: IReportSurveySection) => JSX.Element
  expandedSections: Record<number, boolean>
  onExpandedSectionsChange: React.Dispatch<React.SetStateAction<Record<number, boolean>>>
}

const SurveySectionAccordion: React.FC<SurveySectionAccordionProps> = ({
  surveySections,
  countNonDescriptiveQuestions,
  renderItems,
  expandedSections,
  onExpandedSectionsChange
}) => {
  const theme = useTheme()

  return (
    <Box sx={surveySectionListSx}>
      {surveySections.map((section, idx) => (
        <Accordion
          elevation={0}
          key={section.surveySectionId ?? idx}
          expanded={!!expandedSections[idx]}
          onChange={(_, expanded) => {
            onExpandedSectionsChange((prev) => ({
              ...prev,
              [idx]: expanded
            }))
          }}
          sx={getSurveySectionCardSx(theme)}
        >
          <AccordionSummary
            expandIcon={<ExpandLessIcon sx={{ color: grey[250] }} />}
            aria-controls={`panel${idx}-content`}
            id={`panel${idx}-header`}
          >
            <Box
              sx={surveySectionSummaryLayoutSx}
            >
              <Box>
                <Typography fontSize={20} fontWeight={600} color={grey[250]}>
                  {section?.title?.trim() ? section.title : section.name}
                </Typography>
                {section.isRepeatPerInstructor && (
                  <Typography fontSize={16} color={grey[850]}>
                    {section.instructorName}
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={3}>
                {section.isRepeatPerInstructor && (
                  <Chip
                    variant="outlined"
                    color="success"
                    label="Repeat per Instructor"
                    sx={{
                      borderRadius: 6,
                      border: `1px solid ${green[50]}`,
                      fontWeight: 500,
                      color: green[100],
                      px: 1,
                      backgroundColor: green[0]
                    }}
                  />
                )}
                <Chip
                  variant="outlined"
                  label={`${countNonDescriptiveQuestions(section.surveyItems)} Questions`}
                  sx={{
                    borderRadius: 6,
                    border: `1px solid ${shade[650]}`,
                    fontWeight: 500,
                    color: grey[250]
                  }}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {renderItems(section.surveyItems || [], section)}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

export default SurveySectionAccordion
